import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import useModalDataState from '@order/hooks/useModalDataState'
import ConfirmationModal from '@ui/components/ConfirmationModal'

import type { CofidisMonthlyApplicationLayoutProps } from '../CofidisMonthlyApplicationForm'
import type { CofidisSectionConfig } from '../sections'
import type {
  CofidisMonthlyApplicationFormValues,
  CofidisSectionFieldSuccess,
} from '../types'
import SectionModal from './SectionModal'
import SectionListItem, { getSectionStatus } from './SectionListItem'
import ProgressDot from './ProgressDot'

/** Optional empty fields (falsy) should not show as valid/success. */
function hasFilledValue(value: unknown): boolean {
  if (value == null) return false
  if (typeof value === 'string' && value.trim() === '') return false
  return true
}

function sectionSliceEquals(
  a: Partial<CofidisMonthlyApplicationFormValues>,
  b: Partial<CofidisMonthlyApplicationFormValues>,
  fields: (keyof CofidisMonthlyApplicationFormValues)[]
): boolean {
  return fields.every((key) => isEqual(a[key], b[key]))
}

export default function SectionModalLayout({
  form,
  sections,
}: CofidisMonthlyApplicationLayoutProps) {
  const { t } = useTranslation(['cofidis', 'ui'])
  const [lastValidSnapshot, setLastValidSnapshot] =
    useState<Partial<CofidisMonthlyApplicationFormValues> | null>(null)
  /** Sections where submit was attempted and validation failed (so we show error state in the list). */
  const [submitAttemptedSectionNames, setSubmitAttemptedSectionNames] =
    useState<Set<string>>(() => new Set())
  const sectionModal = useModalDataState<string | null>({ data: null })
  const {
    isOpen: isSectionModalOpen,
    data: openSectionName,
    open: openSectionModal,
    close: closeSectionModal,
  } = sectionModal
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)

  const openSection = sections.find((s) => s.name === openSectionName) ?? null
  const openSectionFields = openSection?.fields ?? []
  const watchedOpenSectionValues = useWatch({
    control: form.control,
    name: openSectionFields as (keyof CofidisMonthlyApplicationFormValues)[],
  })

  const getValues = form.getValues
  const errors = form.formState.errors
  const { submitCount, isSubmitSuccessful } = form.formState

  const prevSubmitCountRef = useRef(submitCount)
  const prevIsSubmitSuccessfulRef = useRef(isSubmitSuccessful)

  useEffect(() => {
    if (submitCount > prevSubmitCountRef.current) {
      prevSubmitCountRef.current = submitCount
      setSubmitAttemptedSectionNames(new Set(sections.map((s) => s.name)))
    }
    if (isSubmitSuccessful && !prevIsSubmitSuccessfulRef.current) {
      prevIsSubmitSuccessfulRef.current = true
      setLastValidSnapshot(form.getValues())
    }
    if (!isSubmitSuccessful) {
      prevIsSubmitSuccessfulRef.current = false
    }
  }, [submitCount, isSubmitSuccessful, sections, form])

  const isSectionSubmitted = useCallback(
    (section: CofidisSectionConfig) => {
      if (!lastValidSnapshot) return false
      if (!submitAttemptedSectionNames.has(section.name)) return false
      return section.fields.every((f) => f in lastValidSnapshot)
    },
    [lastValidSnapshot, submitAttemptedSectionNames]
  )

  const hasSectionChanged = useCallback(
    (section: CofidisSectionConfig) => {
      if (!lastValidSnapshot) return false
      return !sectionSliceEquals(getValues(), lastValidSnapshot, section.fields)
    },
    [lastValidSnapshot, getValues]
  )

  const revertSection = useCallback(
    (section: CofidisSectionConfig) => {
      if (!lastValidSnapshot) return
      const current = getValues()
      const reverted = {
        ...current,
        ...pick(lastValidSnapshot, section.fields as string[]),
      }
      form.reset(reverted)
    },
    [lastValidSnapshot, getValues, form]
  )

  const openIndex = openSection
    ? sections.findIndex((s) => s.name === openSection.name)
    : -1

  const nextSection =
    openIndex >= 0 && openIndex < sections.length - 1
      ? sections[openIndex + 1]
      : null

  const prevSection = openIndex > 0 ? sections[openIndex - 1] : null

  const pendingActionAfterDiscardRef = useRef<'close' | 'prev' | 'next' | null>(
    null
  )

  const canCloseModal = useCallback((): boolean => {
    if (!openSection) return true
    if (isSectionSubmitted(openSection) && hasSectionChanged(openSection)) {
      pendingActionAfterDiscardRef.current = 'close'
      setShowDiscardConfirm(true)
      return false
    }
    return true
  }, [openSection, isSectionSubmitted, hasSectionChanged])

  const handleClose = useCallback(() => {
    if (canCloseModal()) {
      closeSectionModal()
    }
  }, [closeSectionModal, canCloseModal])

  const handleDiscardConfirm = useCallback(() => {
    if (openSection) {
      revertSection(openSection)
      const pending = pendingActionAfterDiscardRef.current
      pendingActionAfterDiscardRef.current = null
      if (pending === 'prev' && prevSection) {
        openSectionModal(prevSection.name)
      } else if (pending === 'next') {
        if (nextSection) {
          openSectionModal(nextSection.name)
        } else {
          closeSectionModal()
        }
      } else {
        closeSectionModal()
      }
    } else {
      pendingActionAfterDiscardRef.current = null
      closeSectionModal()
    }
    setShowDiscardConfirm(false)
  }, [
    openSection,
    prevSection,
    nextSection,
    revertSection,
    openSectionModal,
    closeSectionModal,
  ])

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirm(false)
  }, [])

  const handleSubmitSection = useCallback(async () => {
    if (!openSection) return
    setSubmitAttemptedSectionNames((prev) =>
      new Set(prev).add(openSection.name)
    )
    const valid = await form.trigger(openSection.fields)
    if (!valid) {
      return
    }
    const values = getValues()
    setLastValidSnapshot((prev) => ({
      ...prev,
      ...values,
    }))
    if (nextSection) {
      openSectionModal(nextSection.name)
      return
    }
    closeSectionModal()
  }, [
    openSection,
    nextSection,
    form,
    getValues,
    openSectionModal,
    closeSectionModal,
  ])

  const goToNext = useCallback(() => {
    if (!openSection) return
    if (isSectionSubmitted(openSection) && hasSectionChanged(openSection)) {
      pendingActionAfterDiscardRef.current = 'next'
      setShowDiscardConfirm(true)
      return
    }
    if (nextSection) {
      openSectionModal(nextSection.name)
    } else {
      closeSectionModal()
    }
  }, [
    openSection,
    nextSection,
    isSectionSubmitted,
    hasSectionChanged,
    openSectionModal,
    closeSectionModal,
  ])

  const goToPrev = useCallback(() => {
    if (!openSection) return
    if (isSectionSubmitted(openSection) && hasSectionChanged(openSection)) {
      pendingActionAfterDiscardRef.current = 'prev'
      setShowDiscardConfirm(true)
      return
    }
    if (prevSection) {
      openSectionModal(prevSection.name)
    } else {
      closeSectionModal()
    }
  }, [
    openSection,
    prevSection,
    isSectionSubmitted,
    hasSectionChanged,
    openSectionModal,
    closeSectionModal,
  ])

  // watchedOpenSectionValues in deps makes this reactive to field changes
  const showNextButton = useMemo(
    () =>
      !!openSection &&
      !!watchedOpenSectionValues &&
      isSectionSubmitted(openSection) &&
      !hasSectionChanged(openSection),
    [
      openSection,
      isSectionSubmitted,
      hasSectionChanged,
      watchedOpenSectionValues,
    ]
  )

  const fieldSuccess = useMemo<CofidisSectionFieldSuccess>(() => {
    const result: CofidisSectionFieldSuccess = {}
    if (!openSection) return result

    const submitted = isSectionSubmitted(openSection)
    const currentValues = getValues()
    const watchedMatchesOpenSection =
      Array.isArray(watchedOpenSectionValues) &&
      watchedOpenSectionValues.length === openSection.fields.length

    for (const [index, field] of openSection.fields.entries()) {
      const currentValue = watchedMatchesOpenSection
        ? watchedOpenSectionValues[index]
        : currentValues[field]
      result[field] =
        submitted &&
        !!lastValidSnapshot &&
        field in lastValidSnapshot &&
        hasFilledValue(currentValue) &&
        isEqual(currentValue, lastValidSnapshot[field])
    }

    return result
  }, [
    getValues,
    isSectionSubmitted,
    lastValidSnapshot,
    openSection,
    watchedOpenSectionValues,
  ])

  return (
    <>
      <View className="relative inset-0 flex w-full flex-col gap-0 overflow-hidden rounded-xl border border-black/10">
        {sections.map((section, index) => {
          const submitted = isSectionSubmitted(section)
          const submitAttempted = submitAttemptedSectionNames.has(section.name)
          const errorsCount = section.fields.filter((f) => errors[f]).length
          return (
            <SectionListItem
              key={section.name}
              index={index}
              section={section}
              submitted={submitted}
              submitAttempted={submitAttempted}
              errorsCount={errorsCount}
              onPress={() => openSectionModal(section.name)}
            />
          )
        })}
      </View>

      {openSection && (
        <SectionModal
          open={isSectionModalOpen}
          close={handleClose}
          section={openSection}
          form={form}
          beforeClose={canCloseModal}
          fieldSuccess={fieldSuccess}
          showNextButton={!!showNextButton}
          onNext={goToNext}
          onPrev={goToPrev}
          onSubmitSection={handleSubmitSection}
          progressComponent={
            <View className="absolute flex h-full w-full flex-1 flex-row items-center justify-center gap-3">
              {sections.map((section) => {
                const submitted = isSectionSubmitted(section)
                const submitAttempted = submitAttemptedSectionNames.has(
                  section.name
                )
                const errorsCount = section.fields.filter(
                  (f) => errors[f]
                ).length

                const status = getSectionStatus(
                  submitted,
                  submitAttempted,
                  errorsCount
                )

                return (
                  <ProgressDot
                    key={section.name}
                    status={status}
                    isOpen={openSectionName === section.name}
                  />
                )
              })}
            </View>
          }
        />
      )}

      <ConfirmationModal
        open={showDiscardConfirm}
        title={t('modals:forms.discardChanges.title')}
        desc={t('modals:forms.discardChanges.desc')}
        onConfirm={handleDiscardConfirm}
        onClose={handleDiscardCancel}
      />
    </>
  )
}
